export type ParsedFindingEvidence = {
  factId?: string;
  label?: string;
  excerpt?: string;
  filePath?: string;
  line?: number;
  column?: number;
  raw: Record<string, unknown>;
};

export type ParsedFindingFix = {
  kind?: string;
  title?: string;
  deterministic?: boolean;
  patch?: unknown;
  raw: Record<string, unknown>;
};

export type FindingTrustSummary = {
  level: 'verified' | 'source-linked' | 'structured' | 'legacy';
  label: string;
  detail: string;
};

export function parseJsonRecord(
  value: string | undefined,
): Record<string, unknown> {
  if (!value) return {};
  try {
    const parsed = JSON.parse(value) as unknown;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
}

export function parseJsonArray(value: string | undefined): unknown[] {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value) as unknown;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function parseFindingEvidence(
  value: string | undefined,
): ParsedFindingEvidence[] {
  return parseJsonArray(value)
    .map((entry) =>
      entry && typeof entry === 'object' && !Array.isArray(entry)
        ? (entry as Record<string, unknown>)
        : null,
    )
    .filter((entry): entry is Record<string, unknown> => entry != null)
    .map((entry) => {
      const location = readLocation(entry);
      return {
        factId: readString(entry, 'factId') ?? readString(entry, 'id'),
        label:
          readString(entry, 'label') ??
          readString(entry, 'title') ??
          readString(entry, 'message'),
        excerpt:
          readString(entry, 'excerpt') ??
          readString(entry, 'snippet') ??
          readString(entry, 'value'),
        filePath:
          location.filePath ??
          readString(entry, 'filePath') ??
          readString(entry, 'file') ??
          readString(entry, 'path'),
        line: location.line ?? readNumber(entry, 'line'),
        column: location.column ?? readNumber(entry, 'column'),
        raw: entry,
      };
    });
}

export function parseFindingFix(
  value: string | undefined,
): ParsedFindingFix | null {
  const raw = parseJsonRecord(value);
  if (Object.keys(raw).length === 0) return null;
  return {
    kind: readString(raw, 'kind') ?? readString(raw, 'type'),
    title: readString(raw, 'title') ?? readString(raw, 'label'),
    deterministic:
      typeof raw.deterministic === 'boolean' ? raw.deterministic : undefined,
    patch: raw.patch ?? raw.patches ?? raw.operations,
    raw,
  };
}

export function findingFixContextFromJson({
  fixJson,
  evidenceJson,
  intent,
}: {
  fixJson?: string;
  evidenceJson?: string;
  intent?: string;
}): string | null {
  const fix = parseFindingFix(fixJson);
  const evidenceCount = parseFindingEvidence(evidenceJson).length;
  const parts: string[] = [];

  if (fix) {
    parts.push(fix.deterministic === true ? 'Deterministic fix' : 'Guided fix');
  }
  if (evidenceCount > 0) {
    parts.push(
      `${evidenceCount} evidence ${evidenceCount === 1 ? 'item' : 'items'}`,
    );
  }
  if (intent && parts.length === 0) {
    parts.push(intent);
  }

  return parts.length > 0 ? parts.join(' · ') : null;
}

export function findingTrustSummary({
  evidenceJson,
  fixJson,
  source,
  filePath,
  commitSha,
  sourceVerified,
  sourceVerificationMethod,
}: {
  evidenceJson?: string;
  fixJson?: string;
  source?: string;
  filePath?: string;
  commitSha?: string;
  sourceVerified?: boolean;
  sourceVerificationMethod?: string;
}): FindingTrustSummary {
  const evidence = parseFindingEvidence(evidenceJson);
  const fix = parseFindingFix(fixJson);
  const evidenceCount = evidence.length;
  const hasLocatedEvidence = evidence.some(
    (item) => item.filePath && item.line != null,
  );
  const sourceName = source === 'ci' ? 'CI' : 'scanner';

  if (sourceVerified === true) {
    const method = sourceVerificationMethodDetail(sourceVerificationMethod);
    const commit = commitSha ? ` at commit ${commitSha.slice(0, 12)}` : '';
    return {
      level: 'verified',
      label: 'Verified source evidence',
      detail: `${method}${commit}.`,
    };
  }

  if (evidenceCount > 0 && commitSha && (hasLocatedEvidence || filePath)) {
    return {
      level: 'source-linked',
      label: 'Source-linked evidence',
      detail: `${evidenceCount} structured evidence ${evidenceCount === 1 ? 'item' : 'items'} tied to commit ${commitSha.slice(0, 12)}.`,
    };
  }

  if (evidenceCount > 0 || fix) {
    const parts: string[] = [];
    if (evidenceCount > 0) {
      parts.push(
        `${evidenceCount} structured evidence ${evidenceCount === 1 ? 'item' : 'items'}`,
      );
    }
    if (fix) {
      parts.push(fix.deterministic === true ? 'deterministic fix' : 'guided fix');
    }
    return {
      level: 'structured',
      label: 'Structured scanner evidence',
      detail: `${parts.join(' and ')} from ${sourceName}.`,
    };
  }

  return {
    level: 'legacy',
    label: 'Summary-level finding',
    detail:
      'No structured scanner evidence was stored; treat this as a legacy summary signal.',
  };
}

function sourceVerificationMethodDetail(method: string | undefined): string {
  if (method === 'github-source-location') {
    return 'Rechecked against GitHub source locations';
  }
  if (method === 'github-scan-source') {
    return 'Produced by the GitHub source-backed scanner';
  }
  return 'Validated against repository source';
}

export function formatFindingTimestamp(value: number | undefined): string {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not recorded';
  return new Intl.DateTimeFormat('en', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

export function stringifyJsonPreview(value: unknown): string {
  if (value == null) return '';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function readString(
  record: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = record[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function readNumber(
  record: Record<string, unknown>,
  key: string,
): number | undefined {
  const value = record[key];
  return typeof value === 'number' && Number.isFinite(value)
    ? value
    : undefined;
}

function readLocation(record: Record<string, unknown>): {
  filePath?: string;
  line?: number;
  column?: number;
} {
  const location = record.location;
  if (!location || typeof location !== 'object' || Array.isArray(location)) {
    return {};
  }
  const locationRecord = location as Record<string, unknown>;
  return {
    filePath:
      readString(locationRecord, 'filePath') ??
      readString(locationRecord, 'file') ??
      readString(locationRecord, 'path'),
    line: readNumber(locationRecord, 'line'),
    column: readNumber(locationRecord, 'column'),
  };
}
